/**
 * YearView Component
 *
 * Displays bookings in a year view format with months as rows
 * and days as small colored cells using YearDayCell component.
 *
 * Uses the same status calculation as WeekView and MonthView for consistency.
 */

import { ButtonTabs } from "@/components/ui/buttons/ButtonTabs";
import { Chip } from "@/components/ui/display/Chip";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import { Legends } from "@/components/booking/display/Legends";
import { YearDayCell } from "@/components/schedule/YearDayCell";
import type { DayCellStatus } from "@/components/schedule/DayCell";
import { EditModeButton } from "@/components/shared/EditModeButton";
import { getBookingStatus, calculateAbsenceFill } from "@/utils/booking";
import { formatDayCellTooltip } from "@/utils/date";
import { formatEmployeeName } from "@/hooks/data/dataQuality";

import classNames from "classnames";
import { isToday } from "date-fns";
import React, { useMemo } from "react";
import styles from "../Schedule.module.css";
import type { YearViewProps } from "../types";

/** Month names in Norwegian */
const MONTH_NAMES = [
  "Januar",
  "Februar",
  "Mars",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Desember",
];

export const YearView: React.FC<YearViewProps> = React.memo(
  ({
    employees,
    hideWeekend,
    hoursInDay,
    selectMode,
    selectedCardId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used by React.memo to trigger re-render when selection changes (Bug #9022)
    selectedIds: _selectedIds,
    isSelected,
    isInRangePreview,
    setRangeHoverTarget,
    addToSelection,
    removeFromSelection,
    clearSelection,
    handleCardClick,
    onCardClick,
    getCardForDay,
    yearMonthData,
    selectedYearEmployee,
    setSelectedYearEmployee,
    onViewModeChange,
    hideAggregate = false,
    // Per-employee edit mode props
    showIndividualEditButton = false,
    editingEmployeeId,
    onEnterEmployeeEditMode,
    onExitEmployeeEditMode,
  }) => {
    // Get the currently selected employee object
    const currentEmployee = useMemo(() => {
      return employees.find((e) => e.id === selectedYearEmployee) ?? null;
    }, [employees, selectedYearEmployee]);

    /**
     * Handle month header click
     * In edit mode: toggle selection of all days in the month for the current employee
     * In view mode: navigate to month view
     */
    const handleMonthHeaderClick = (
      cells: (typeof yearMonthData)[number][number],
    ) => {
      if (selectMode && currentEmployee) {
        // In edit mode: toggle selection for this month
        const cardIds: string[] = [];
        for (const week of cells) {
          for (const cell of week) {
            // Skip weekends if hidden
            if (hideWeekend && cell.isWeekend) continue;
            const cardData = getCardForDay(currentEmployee, cell.date);
            cardIds.push(cardData.id);
          }
        }

        // Check if all cards are already selected
        const allSelected = cardIds.every((id) => isSelected(id));

        if (allSelected) {
          // All selected - deselect them
          removeFromSelection(cardIds);
        } else {
          // Some or none selected - select all
          addToSelection(cardIds);
        }
      } else {
        // In view mode: navigate to month view
        onViewModeChange("month");
      }
    };

    // Employee tabs for year view
    const employeeTabOptions = useMemo(() => {
      return employees.map((emp) => ({
        id: emp.id.toString(),
        label: formatEmployeeName(emp).toUpperCase(),
      }));
    }, [employees]);

    // Get sorted years
    const years = Object.keys(yearMonthData).map(Number).sort();

    return (
      <div className={styles.yearView} data-testid="schedule-year-view">
        {/* Employee Tabs */}
        {employees.length > 1 && (
          <div className={styles.yearEmployeeTabs}>
            <ButtonTabs
              options={employeeTabOptions}
              selected={selectedYearEmployee?.toString() || ""}
              className={styles.yearEmployeeTabsWrap}
              onSelect={(id) => {
                const newEmployeeId = Number(id);
                setSelectedYearEmployee(newEmployeeId);

                // Clear selections when switching employees (Bug #6 fix)
                // This prevents selections from Employee #1 appearing on Employee #2's calendar
                clearSelection();

                // If in edit mode, switch edit to new employee
                if (editingEmployeeId !== null) {
                  onEnterEmployeeEditMode?.(newEmployeeId);
                }
              }}
              align="center"
              data-testid="year-employee-tabs"
            />
          </div>
        )}

        {/* Single employee chip when only one */}
        {employees.length === 1 && (
          <div className={styles.yearEmployeeTabs}>
            <Chip
              label={formatEmployeeName(employees[0]).toUpperCase()}
              data-testid="year-single-employee"
            />
          </div>
        )}

        {/* Year sections */}
        {years.map((year) => (
          <div key={year} className={styles.yearSection}>
            {/* Year separator */}
            <div className={styles.yearSeparator}>
              <div className={styles.yearLabel}>{year}</div>
            </div>

            {/* Month rows */}
            {Object.keys(yearMonthData[year])
              .map(Number)
              .sort((a, b) => a - b)
              .map((monthIndex) => {
                const cells = yearMonthData[year][monthIndex];

                const monthKey = `${year}-${monthIndex}`;

                return (
                  <div
                    key={monthKey}
                    className={classNames(styles.yearMonthRow)}
                  >
                    {/* Month name */}
                    <div
                      className={styles.yearMonthHeader}
                      onClick={() => handleMonthHeaderClick(cells)}
                    >
                      {MONTH_NAMES[monthIndex]}
                    </div>

                    {/* Day cells - wrapped by week for proper line breaking */}
                    <div className={styles.yearDayCells}>
                      {cells.map((week, weekIndex) => (
                        <div key={weekIndex} className={styles.yearWeekGroup}>
                          {/* eslint-disable-next-line sonarjs/no-nested-functions -- Pre-existing: extracting to separate component would be a larger refactor */}
                          {week.map((cell) => {
                            // When weekends are hidden, render empty placeholder to maintain week structure
                            if (hideWeekend && cell.isWeekend) {
                              return (
                                <div
                                  key={cell.date.toISOString()}
                                  className={styles.yearDayPlaceholder}
                                  aria-hidden="true"
                                />
                              );
                            }

                            // Get card data for selection support
                            const cardData = currentEmployee
                              ? getCardForDay(currentEmployee, cell.date)
                              : null;
                            // Determine if card is selected based on mode
                            let cardSelected = false;
                            if (cardData) {
                              cardSelected = selectMode
                                ? isSelected(cardData.id)
                                : cardData.id === selectedCardId;
                            }

                            // Build tooltip content with holiday name if applicable
                            const tooltipContent = formatDayCellTooltip(
                              cell.date,
                            );

                            // Calculate status using the same logic as WeekView and MonthView
                            // For soft bookings (hideAggregate=true): always green when booked
                            // For hard bookings: use proper status calculation with hard booking hours + absence
                            let status: DayCellStatus;
                            if (hideAggregate) {
                              status = cell.hours > 0 ? "fullyBooked" : "free";
                            } else {
                              status = getBookingStatus(
                                cell.hardBookingHours,
                                cell.absenceHours,
                                hoursInDay,
                              );
                            }

                            // Calculate absence fill for overlay (only for hard booking mode)
                            const absenceFill = hideAggregate
                              ? 0
                              : calculateAbsenceFill(
                                  cell.absenceHours,
                                  hoursInDay,
                                );

                            // Determine if card is in range preview (coerce null to false for type safety)
                            const cardInRangePreview =
                              selectMode && cardData
                                ? isInRangePreview(cardData.id)
                                : false;

                            const dayCellNode = (
                              <YearDayCell
                                key={cell.date.toISOString()}
                                date={cell.date}
                                hours={cell.hours}
                                status={status}
                                absenceFill={absenceFill}
                                isToday={isToday(cell.date)}
                                isWeekend={cell.isWeekend}
                                selected={cardSelected}
                                selectable={
                                  selectMode &&
                                  editingEmployeeId === selectedYearEmployee
                                }
                                inRangePreview={cardInRangePreview}
                                className={classNames(
                                  styles.yearDayCellWrapper,
                                  cell.isRedDay && styles.yearDayRedDay,
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();

                                  if (cardData) {
                                    if (selectMode) {
                                      handleCardClick(
                                        cardData.id,
                                        {
                                          shift: e.shiftKey,
                                          ctrl: e.ctrlKey,
                                          meta: e.metaKey,
                                        },
                                        onCardClick,
                                      );
                                    } else {
                                      onCardClick?.(cardData);
                                    }
                                  }
                                }}
                                onMouseEnter={
                                  selectMode && cardData
                                    ? (e) => {
                                        if (e.shiftKey) {
                                          setRangeHoverTarget(cardData.id);
                                        } else {
                                          setRangeHoverTarget(null);
                                        }
                                      }
                                    : undefined
                                }
                              />
                            );

                            return tooltipContent ? (
                              <Tooltip
                                key={cell.date.toISOString()}
                                content={tooltipContent}
                                position="top"
                                delay={300}
                              >
                                {dayCellNode}
                              </Tooltip>
                            ) : (
                              dayCellNode
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        ))}

        {/* Individual Edit Button for the selected employee */}
        {showIndividualEditButton && selectedYearEmployee && (
          <div className={styles.employeeEditButtonContainer}>
            <EditModeButton
              enabled={editingEmployeeId === selectedYearEmployee}
              disabled={false}
              onClick={() =>
                editingEmployeeId === selectedYearEmployee
                  ? onExitEmployeeEditMode?.()
                  : onEnterEmployeeEditMode?.(selectedYearEmployee)
              }
              data-testid={
                editingEmployeeId === selectedYearEmployee
                  ? `employee-edit-mode-cancel-btn-${selectedYearEmployee}`
                  : `employee-edit-mode-btn-${selectedYearEmployee}`
              }
            />
          </div>
        )}

        <Legends direction="row" align="center" />
      </div>
    );
  },
);

YearView.displayName = "YearView";

export default YearView;
